import type { NestedKeys } from '../../types.ts';

/**
 * `en` · `auth` namespace — the front door: sign-in, the sign-up wizard, and the
 * 18+ age gate. Its own namespace because it is the ONE surface a signed-out
 * visitor loads, so it must be lazy-loadable without dragging chat/builder/
 * billing copy along.
 *
 * The age-gate strings are compliance copy (`docs/idea/compliance.md`) — reword
 * them only alongside that policy.
 */
export const auth = {
  title: '4fantasia',
  email: 'Email',
  password: 'Password',
  displayName: 'Your name',
  over18: 'I am 18 or older and agree to the Terms.',
  loading: 'Please wait…',
  restoring: 'Restoring your session…',
  unreachable: {
    // Shown when the boot restore could not REACH the edge. Deliberately says
    // nothing about the visitor's account: their session is probably fine, and
    // "signed out" would be both wrong and alarming.
    title: 'We’ll be right back',
    body: '4fantasia is temporarily unreachable. Your account is fine — nothing has been lost.',
    retry: 'Try again',
  },
  signOut: 'Sign out',
  haveAccount: 'Already have an account?',
  noAccount: 'New here?',
  login: {
    prompt: 'Welcome back — sign in to your companions.',
    submit: 'Sign in',
  },
  signup: {
    prompt: 'Create your account to build your first companion.',
    submit: 'Create account',
  },
  captcha: {
    // Shown under the form when the hCaptcha script cannot load (blocked,
    // offline, vendor outage). The gate fails CLOSED — submit stays disabled —
    // so this has to say why, or the front door looks simply broken.
    unavailable: 'The security check could not load. Disable your blocker or retry.',
  },
  age: {
    title: 'One more step',
    prompt: 'Confirm you are an adult to unlock your companions.',
    confirm: 'I confirm I am 18 or older.',
    submit: 'Verify age',
  },
} as const;

export type AuthMessages = typeof auth;

/** Typo-safe dot-path keys for the `auth` namespace. */
export type AuthKey = NestedKeys<AuthMessages>;
